import {
  isMobile,
  isMantle,
  isAffiliate,
  isAppBaseRoute,
  isCore,
  isCorePaid,
  isWebWinback,
  isEnLanguage,
  isInApp,
  isWinback,
  isEmailMain,
  isIOS,
  isUSWinback,
  isCountry,
  isUS,
  isIntl,
  isDeLanguage,
  isCoreDirect,
  isIntlMantle,
  isUSInAppWinback,
  isAffiliateWithoutTrial,
  isDeEmailMain,
  isEsEmailMain,
  isInAppWinback,
  isHM,
  isDE,
  isDPP,
  isAppAndroidBase,
  isDirect,
  isReferralsBuyflow,
  isCorePaidWithoutAddWordsSEM,
  isAffiliateWithTrial,
  isReferralsRoute,
  isEsLanguage,
  isAppEmail,
  isClinicalRoute,
} from ".";
import { routeConstants } from "@utils/constants";
import getStore, { CoreReduxState } from "../redux/store";

import { isEmailWinback, isFromEmail, isUSEmail } from "./email";
import { checkSegments } from "./util";
import {
  EligibleStateCodeNoomClinical,
  INELIGIBLE_HEALTH_RISKS,
} from "@components/refactored-survey/question-sets/insurance-survey-questions/utils/insuranceConstants";
import { getCourseDuration } from "@utils/calculateGoals";
import { getMeristemContext, getSubdivision } from "@utils/meristemContext";
import { getTrackingParams } from "../services/VisitTracker";
import { canRetrieveWeight } from "../refactoredSurvey";
import { getSingleOffers } from "../../hooks/useOffersContext";
import { getSingleAddOns } from "../../hooks/useAddOnsContext";
import { getSingleInAppOffers } from "../../hooks/useInAppOffersContext";
import { appConfig } from "src/config";
import { loadWinbackInfo } from "../redux/slices/winbackInfo";
import { calculateBMIImperial } from "src/primitive/bmi";
import { isAppAndroid, isAppIos, isAppIosBase } from "./in-app";
import {
  isMealPlan,
  isMealWorkoutBundle,
  isWorkoutPlan,
  isNoomSummit,
} from "../addons";
import { isInExperimentConflictingWithSummitCoursePack } from "../api/addons/addons";
import { getIOSMessageHandlers } from "../in-app/messageHandlers";
import { getSurveyAnswers } from "src/hooks/survey/answers";
import {
  FeatureFlags,
  getFeatureFlagValue,
} from "src/pageDefinitions/featureFlags";

import {
  getPromoCodeState,
  isPromoCodePersisted,
} from "../redux/slices/promoCode";
import { TELEHEALTH_FREE_PLAN_ID } from "src/funnels/payment-en/pages/telehealthRedirects";
import {
  getMultiUserNoomPlanId,
  planDurationMonthsToTelehealthNoomPlanId,
} from "@utils/plansMapping";
import { loadActiveSubscriptionDataState } from "../redux/slices/activeSubscriptionData";
import { getMeristemConfigValue } from "../meristemConfig";
import {
  DE_COURSE_PACK_ELIGIBLE_COUNTRIES,
  EN_COURSE_PACK_ELIGIBLE_COUNTRIES,
} from "@utils/userSegment/constants";
import { isMultiUserPlan } from "../multiUserPlan";
import {
  loadEquivalentMultiUserPlan,
  loadProratedRefundData,
} from "../api/noomTogether";
import { isCheckoutTimerExpired } from "../checkoutTimer";
import { isNewBaselineCoursePackVariant } from "../coursePack";
import { getURLParams } from "../urlParams";
import zipState from "zip-state";
import { hasFallbackPromotionalOfferConfiguration } from "../fallbackPromotionalOffer";

const HW_NON_VIP_BATCH_ID_DEV = 1;
const HW_NON_VIP_BATCH_ID_PROD = 354;
export const DPP_OVERRIDE_PLAN = "dpp-12-month";
export const USD_MONTHLY_PLAN_ID = "option_d_20221201_Ex1084var1email";

const devModeFlagSet =
  new URLSearchParams(window.location.search).get("idm") === "true";

export function isDevMode() {
  return devModeFlagSet;
}

export function isPrerender() {
  // eslint-disable-next-line no-underscore-dangle
  return !!(window as any).__prerender;
}

export function evalResultWithFeatureFlag(
  result: boolean,
  flag: keyof FeatureFlags
) {
  const flagValue = getFeatureFlagValue(flag) as boolean;
  if (flagValue === undefined) {
    return result;
  }
  return flagValue;
}

export const shouldEnrollUser = (state?: CoreReduxState) => {
  return !isInApp(state) && !isAppEmail(state);
};

export const isHMEligible = () => isUS() && isEnLanguage();

export const isNoomProgramQuestionEligible = (state?: CoreReduxState) =>
  isHMEligible() && isDirect(state) && isIOS();

const hadPreviousITunesSubscription = () => {
  const urlParams = new URLSearchParams(window.location.search);
  // This parameter is set by the growth backend in the internal app endpoint
  // More details about that here: https://github.com/noom/eng-handbook/blob/master/teams/growth/product/Winbacks.md#more-about-in-app-routes
  return urlParams.get("had_itunes_sub") === "true";
};

const disableLPSurveySegment = checkSegments({
  featureFlagOverride: "disableLPSurvey",
});

export const isSurveyOnLPEligible = checkSegments({
  oneOf: [
    routeConstants.exsf01d,
    routeConstants.exsf01,
    routeConstants.affiliateTrialNPB,
    routeConstants.appEmail,
    routeConstants.clinical,
  ],
  noneOf: [isHM, isInApp, disableLPSurveySegment],
});

export const isBraintreeStripeiOSEligible = checkSegments({
  allOf: [hadPreviousITunesSubscription, routeConstants.app14DayReturning],
});

export const isGooglePlayPlansEligible = () => {
  // TODO (Brandon): enable when in-app is ready for release
  // const hasGooglePlayMessageHandler =
  //   !!getAndroidMessageHandlers()?.buyflowPurchaseProduct ||
  //   ["staging", "development", "local"].includes(appConfig.NOOM_ENV);

  // return isAppAndroidBase() && hasGooglePlayMessageHandler;
  return false;
};

export const isITunesPlansEligible = () => {
  const hasITunesMessageHandler =
    !!getIOSMessageHandlers()?.buyflowPurchaseProduct ||
    ["staging", "development", "local"].includes(appConfig.NOOM_ENV);

  if (isBraintreeStripeiOSEligible()) {
    return false;
  }

  return isAppIos() && hasITunesMessageHandler;
};

export const isITunes1MonthDefaultPlanEligible = () => {
  const hasITunesDefaultPlanMessageHandler =
    !!getIOSMessageHandlers()?.buyflowPurchaseNoDetails ||
    ["staging", "development", "local"].includes(appConfig.NOOM_ENV);

  if (isBraintreeStripeiOSEligible()) {
    return false;
  }

  return isAppIos() && hasITunesDefaultPlanMessageHandler;
};

/**
 * Checks if the user can use the native app in-app purchase flow.
 */
export const isInAppNativePurchaseEligible = checkSegments({
  oneOf: [
    isITunes1MonthDefaultPlanEligible,
    isITunesPlansEligible,
    isGooglePlayPlansEligible,
  ],
});

/**
 * Checks if the user can use the web-based in-app purchase flow.
 */
export const isInAppWebPurchaseEligible = checkSegments({
  oneOf: [isAppAndroid, isBraintreeStripeiOSEligible],
});

const ffwdEligibleCurrencies = ["USD", "EUR", "GBP", "CAD", "AUD"];
export const isCurrencyFastForwardEligible = () => {
  const { currency = "USD" } = getStore().getState().serverContext;
  return ffwdEligibleCurrencies.includes(currency);
};

const isUSOnlyFastForwardEligibleRoute = checkSegments({
  allOf: [isUS],
  oneOf: [isEmailMain, isEmailWinback, isReferralsRoute],
});

export const hasPromoCodeIntl = checkSegments({
  allOf: [hasPromoCode, isIntl],
});

export const isFastForwardEligible = checkSegments({
  allOf: [isEnLanguage, isCurrencyFastForwardEligible],
  noneOf: [isTelehealthPlan, userHasPurchasedNoomClinical],
  oneOf: [
    isCore,
    routeConstants.winback,
    isAppAndroidBase,
    isAffiliateWithTrial,
    isUSOnlyFastForwardEligibleRoute,
  ],
});

const isNoomPremiumEligibleUS = checkSegments({
  allOf: [isUS, isEnLanguage],
  oneOf: [isMantle, isWebWinback, routeConstants.emailMain],
});

export const isNoomPremiumSubscriptionEligible = checkSegments({
  allOf: [isUS, isEnLanguage],
});

export const isFreeSupporterPlanEligible = checkSegments({
  allOf: [isUS, isEnLanguage],
  oneOf: [isCore, isWebWinback, isEmailMain],
});

const isLanguageNoomPremiumEligible = checkSegments({
  oneOf: [isEnLanguage, isDeLanguage, isEsLanguage],
});

const isNoomPremiumEligibleIntl = checkSegments({
  allOf: [isIntl, isLanguageNoomPremiumEligible],
  oneOf: [
    isMantle,
    isWebWinback,
    routeConstants.emailMain,
    routeConstants.freeTrial,
  ],
});

export const isAddonStoreEligible = checkSegments({
  allOf: [isUS, isEnLanguage],
  noneOf: [isAppIosBase, isTelehealthPlan, userHasPurchasedNoomClinical],
  oneOf: [
    routeConstants.direct,
    routeConstants.exsf01d,
    routeConstants.exsf01,
    isAffiliateWithoutTrial,
    routeConstants.emailMain,
    isReferralsBuyflow,
    routeConstants.freeTrial,
    isWebWinback,
    isAppAndroidBase,
  ],
});

export const isWelcomeToNoomEligible = checkSegments({
  oneOf: [isMobile, isMantle, isFromEmail, isWinback, isInAppWinback],
});

export const userHasPurchasedNoomPremium = () => {
  const { purchasedNoomPremium } = getStore().getState();
  return purchasedNoomPremium;
};

export const isNoomPremiumConsultationSurveyEligible = checkSegments({
  oneOf: [userHasPurchasedNoomPremium, hasBoughtMealAndWorkoutPlan],
});

export const navigateToNoomPremiumConsultationSurvey = checkSegments({
  allOf: [userHasPurchasedNoomPremium],
  oneOf: [
    isMantle,
    routeConstants.affiliateTrialNPB,
    isReferralsBuyflow,
    isEmailWinback,
    isWinback,
    isAppAndroidBase,
  ],
});

export const isImportantDateEligible = checkSegments({
  allOf: [isEnLanguage],
  oneOf: [isMantle],
});

export const isFivePercentEligible = checkSegments({
  allOf: [isUS],
  oneOf: [isMantle],
});

/**
 * Implements the logic for targeting users for dynamic plan graph(ex417)
 */
export const isDynamicPlanGraphEligible = checkSegments({
  oneOf: [
    isMantle,
    isWinback,
    isUSEmail,
    isDeEmailMain,
    isInAppWinback,
    isUSInAppWinback,
  ],
  allOf: [canRetrieveWeight],
});

export const isFinalGraphEligible = checkSegments({
  oneOf: [isMantle, isUSWinback, isUSEmail, isUSInAppWinback],
});

export const isShowingPaymentGraphExtraContent = checkSegments({
  oneOf: [isMantle, isUSWinback, isInAppWinback],
});

// TODO: Remove isDeLanguage once Noom Premium and Addons can successfully charge SEPA
// https://noomhq.atlassian.net/browse/GT-1256
export const isSEPAEligible = checkSegments({
  allOf: [isDE, isDeLanguage],
});

/**
 * Verifies if the current user should see the final update graph
 */
export const shouldShowFinalUpdateGraph = (state = getStore().getState()) => {
  const surveyAnswers = getSurveyAnswers(state);
  const { weight, idealWeight, weightKg, idealWeightKg } = surveyAnswers;
  const courseDuration = getCourseDuration(surveyAnswers);

  // EnUSBaseline + App14DayBase routes
  if (
    weight &&
    idealWeight &&
    weightKg &&
    idealWeightKg &&
    isFivePercentEligible() &&
    courseDuration <= 8
  ) {
    return true;
  }

  return shouldCondenseOrShowFinalUpdateGraph(courseDuration);
};

function shouldCondenseOrShowFinalUpdateGraph(courseDuration: number) {
  const isCourseDurationEligible =
    courseDuration >= 2 &&
    ((courseDuration <= 8 && isIntlMantle()) || courseDuration <= 6);
  return (
    canRetrieveWeight() && isFinalGraphEligible() && isCourseDurationEligible
  );
}

export const hasNoTrial = checkSegments({
  oneOf: [
    routeConstants.affiliateNoTrial,
    routeConstants.affiliateNoTrialPromo,
    routeConstants.ndpp,
    isGooglePlayPlansEligible,
  ],
});

export const isDirectTraffic = checkSegments({
  oneOf: [
    routeConstants.direct,
    routeConstants.appUpsell,
    routeConstants.hmDirect,
  ],
});

export const isTrialFeeWaived = checkSegments({
  oneOf: [
    isInAppWinback,
    isAppIosBase,
    isReferralsBuyflow,
    routeConstants.freeTrial,
    routeConstants.hmReferrals,
    routeConstants.affiliate,
    routeConstants.phillips,
    routeConstants.humana,
  ],
});

export const isChooseTrialPriceNewStyleEligible = checkSegments({
  oneOf: [isMantle, isEmailMain, isHM, isInApp],
});

export const isTrialEligible = (
  { recommendedPlan } = getStore().getState()
) => {
  return recommendedPlan.has_trial && !hasNoTrial();
};

export function showChooseTrialPage(state = getStore().getState()) {
  if (!state.serverContext.trial_fees) {
    return false;
  }
  if (isEnLanguage()) {
    return !isTrialFeeWaived() && !hasNoTrial();
  }
  return (
    isCoreDirect(state) ||
    isCorePaid(state) ||
    isDeEmailMain() ||
    isEsEmailMain() ||
    isHM()
  );
}

export const isPromoCodeBoxEnabled = checkSegments({
  noneOf: [
    isInApp,
    isCorePaidWithoutAddWordsSEM,
    isWinback,
    routeConstants.affiliateNoTrial,
  ],
  featureFlagOverride: "togglePromoCode",
});

export const shouldValidateTokenFlag = checkSegments({
  oneOf: [routeConstants.appUpsell],
});

export const shouldShowOnscreenBackButtons = checkSegments({
  oneOf: [isInApp],
});

export const isPaidTraffic = checkSegments({
  oneOf: [
    isCorePaid,
    routeConstants.experiment,
    routeConstants.affiliateTrialNPB,
    routeConstants.hmPaid,
    routeConstants.hmExsf01,
    routeConstants.hmExsf01d,
    routeConstants.hmAwsem,
    routeConstants.winback,
    routeConstants.hmWinback,
  ],
});

export const shouldShowExplainerLoadingPage = checkSegments({
  oneOf: [isMantle, isWinback],
});

export const shouldUseMobileOptimizedControls = checkSegments({
  oneOf: [isMantle, isWinback, routeConstants.hmPaid, routeConstants.hmDirect],
});

export const isBMINoomClinicalEligible = (
  { surveyAnswers } = getStore().getState()
) => {
  const { heightFeet, heightInch, weight } = surveyAnswers;
  if (
    heightFeet !== undefined &&
    heightInch !== undefined &&
    weight !== undefined
  ) {
    const currentBMI = calculateBMIImperial(heightFeet, heightInch, weight);
    return currentBMI && currentBMI >= 27;
  }
  return false;
};

export const isAgeRangeNoomClinicalEligible = (
  { surveyAnswers } = getStore().getState()
) => {
  const { ageRange } = surveyAnswers;
  if (ageRange !== undefined) {
    const parsedAgeRange = Number.parseInt(ageRange?.[0], 10);
    return parsedAgeRange && parsedAgeRange < 60;
  }
  return false;
};

export const isCurrentHealthRiskNoomClinicalEligible = (
  { surveyAnswers } = getStore().getState()
) => {
  const { currentHealthRiskMedical, currentHealthRiskMedicalMale } =
    surveyAnswers;

  if (
    currentHealthRiskMedical !== undefined ||
    currentHealthRiskMedicalMale !== undefined
  ) {
    const answers = [
      ...(currentHealthRiskMedical || []),
      ...(currentHealthRiskMedicalMale || []),
    ];

    const ineligible_answers = answers.filter((answer) =>
      INELIGIBLE_HEALTH_RISKS.includes(answer)
    );

    return !ineligible_answers.length;
  }
  return false;
};

export const noomClinicalOptedIn = (
  { surveyAnswers } = getStore().getState()
) => {
  const { medicationFocused } = surveyAnswers;
  return !!medicationFocused;
};

export const isZipcodeNoomClinicalEligible = (
  { surveyAnswers } = getStore().getState()
) => {
  const { zipcode } = surveyAnswers;
  if (zipcode !== undefined) {
    // Double check the state that they entered on the /zipcode question
    // Because it might not necessarily match the state we got from Meristem
    const state = zipState(zipcode);
    return state in EligibleStateCodeNoomClinical;
  }
  return false;
};

// How to Turn Off Telehealth: https://github.com/noom/eng-handbook/blob/master/teams/healthcare/playbook/turn_off_telehealth.md
// Check if Telehealth is enabled
export function isTelehealthEnabledGlobally() {
  return !!appConfig.TELEHEALTH_ENABLED;
}

const isNoomClinicalEnabled = () => {
  return !!getMeristemConfigValue("noomClinicalEnabled");
};

const isRouteNoomClinicalEligible = checkSegments({
  oneOf: [routeConstants.exsf01, routeConstants.exsf01d, routeConstants.direct],
});

export const isUserSegmentNoomClinicalEligible = () => {
  if (isClinicalRoute()) {
    // Don't do the state or feature flag check
    return isTelehealthEnabledGlobally() && isUS() && isEnLanguage();
  }

  return (
    isNoomClinicalEnabled() &&
    isTelehealthEnabledGlobally() &&
    isUS() &&
    isEnLanguage() &&
    isRouteNoomClinicalEligible() &&
    getSubdivision() in EligibleStateCodeNoomClinical
  );
};

export const isNoomClinicalSurveyEligible = checkSegments({
  allOf: [
    isUserSegmentNoomClinicalEligible,
    isBMINoomClinicalEligible,
    isAgeRangeNoomClinicalEligible,
  ],
});

export const shouldShowNoomClinicalCheckoutOffer = (
  state = getStore().getState()
): boolean => {
  const { promoCode, recommendedPlan } = state;
  return (
    isNoomClinicalSurveyEligible(state) &&
    isCurrentHealthRiskNoomClinicalEligible(state) &&
    !promoCode.promoCodeIsVip &&
    recommendedPlan.has_trial &&
    // We show the noom med add-on at checkout in 1 of 2 flows:
    // 1. The user opted into NC and entered a valid zipcode afterwards
    // 2. The user didn't opt into NC but also didn't opt into the mood bundle
    ((isZipcodeNoomClinicalEligible(state) && noomClinicalOptedIn(state)) ||
      (!moodBundleOptedIn(state) &&
        !noomClinicalOptedIn(state) &&
        !multiUserPlanOptedIn(state)))
  );
};

export const shouldPreselectNoomClinicalCheckoutOffer = checkSegments({
  allOf: [
    shouldShowNoomClinicalCheckoutOffer,
    isZipcodeNoomClinicalEligible,
    noomClinicalOptedIn,
  ],
});

export function isTelehealthPlan(state = getStore().getState()): boolean {
  const noomPlanId = state?.recommendedPlan?.noom_plan_id;
  const paidTelehealthPlans = Object.values(
    planDurationMonthsToTelehealthNoomPlanId
  );
  return (
    paidTelehealthPlans?.includes(noomPlanId) ||
    noomPlanId === TELEHEALTH_FREE_PLAN_ID
  );
}

export function userHasPurchasedNoomClinical(state = getStore().getState()) {
  const { purchasedNoomClinical, userData } = state;

  // TODO: Move to using a more reliable way like an API instead of using
  // this userdata flag to determine who purchased Noom Med
  return purchasedNoomClinical || userData.purchasedNoomClinical;
}

const TELEHEALTH_CONTEXT_TYPES = ["telehealth", "telehealth-followup"];

export function isTelehealth() {
  return TELEHEALTH_CONTEXT_TYPES.includes(getMeristemContext().context_type);
}

/**
 * Signals if the current user/experience is subject to HIPPA data protection rules.
 * This can be used to filter logging/etc as appropriate to ensure compliance.
 */
export const isHipaa = (): boolean => {
  return isTelehealth();
};

export const isHmSuperswitchEligible = (): boolean => {
  const { activeSubscriptionData } = getStore().getState();
  return (
    isEnLanguage() &&
    activeSubscriptionData.hasActiveSubscription &&
    activeSubscriptionData.activeSubscriptionCurriculumSlug &&
    activeSubscriptionData.activeSubscriptionCurriculumSlug.endsWith("en_US") &&
    ((activeSubscriptionData.batchId === HW_NON_VIP_BATCH_ID_PROD &&
      appConfig.NOOM_ENV === "production") ||
      activeSubscriptionData.batchId === HW_NON_VIP_BATCH_ID_DEV)
  );
};

isHmSuperswitchEligible.load = () => {
  const store = getStore();
  const { email } = store.getState().userData;
  if (email) {
    return getStore()
      .dispatch(loadActiveSubscriptionDataState({ email }))
      .then(() => {});
  }
  return Promise.resolve();
};

export const userHasAccessCode = (): boolean => {
  return !!getStore().getState().userData.access_code;
};

const startFetchingRequiredMultiUserPlanData = () => {
  return true;
};

startFetchingRequiredMultiUserPlanData.load = async () => {
  await getStore().dispatch(loadActiveSubscriptionDataState({}));
  const { billingCycleIntervalCount } =
    getStore().getState().activeSubscriptionData;

  const initialMultiUserNoomPlanId = getMultiUserNoomPlanId(
    billingCycleIntervalCount
  );
  // Explicitly don't await for this to not block pageset load
  Promise.all([
    loadProratedRefundData(),
    loadEquivalentMultiUserPlan(initialMultiUserNoomPlanId),
  ]);

  return Promise.resolve();
};

export const isEligibleForNoomTogether = checkSegments({
  allOf: [isEnLanguage, userHasAccessCode],
});

isEligibleForNoomTogether.load = async () => {
  await getStore().dispatch(loadActiveSubscriptionDataState({}));
};

const hasFamilyPlanEligibleSubscription = () => {
  const {
    isIAPsubscription,
    hasActiveSubscription,
    planId,
    billingCycleIntervalCount,
  } = getStore().getState()?.activeSubscriptionData;
  return (
    hasActiveSubscription &&
    billingCycleIntervalCount > 1 &&
    !isIAPsubscription &&
    !isMultiUserPlan(planId)
  );
};

hasFamilyPlanEligibleSubscription.load = () => {
  return getStore()
    .dispatch(loadActiveSubscriptionDataState({}))
    .then(() => {});
};

export const isFamilyPlanSurveyEligible = checkSegments({
  allOf: [
    isUS,
    isEnLanguage,
    hasFamilyPlanEligibleSubscription,
    startFetchingRequiredMultiUserPlanData,
  ],
});

export const isEligibleForExitIntentModalEmail = () => {
  return isIntl() && isEmailMain() && getTrackingParams().utm_source === "drip";
};

export function hasPromoCode(state?: CoreReduxState) {
  const promoCode = getPromoCodeState(state);
  return !!promoCode?.promoCodeApplied;
}

export function hasVipPromoCode(state?: CoreReduxState) {
  const promoCode = getPromoCodeState(state);

  return hasPromoCode() && promoCode?.promoCodeIsVip;
}

export const hasOffers = (state: CoreReduxState) => {
  const { offers } = state;
  return offers.hasOffers;
};
export const isTaxExclusiveCountry = () => isCountry(["US", "CA"]);

export const hasSingleOffers = () => {
  const { serverContext } = getStore().getState();
  const singleOffers = getSingleOffers(serverContext);

  return singleOffers.length > 0;
};

export const hasSingleAddOns = () => {
  const { serverContext } = getStore().getState();
  const singleOffers = getSingleAddOns(serverContext);

  return singleOffers.length > 0;
};

export const hasSingleInAppOffers = () => {
  const { serverContext, userData } = getStore().getState();
  const singleOffers = getSingleInAppOffers(
    serverContext,
    userData.NoomPremiumSurveyCompleteDateISO
  );

  return singleOffers.length > 0;
};

export const hasEnrollment = () => {
  const { serverContext } = getStore().getState();
  return serverContext?.enrollment;
};

export const isNoomPremiumConsultationSurveyDisqualified = (
  state = getStore().getState()
) => {
  const { surveyAnswers } = state;
  return surveyAnswers?.workoutPlanLimitations?.includes("painOrLosingBreath");
};

export const isNoomPremiumEligible = () => {
  const promoCode = getPromoCodeState();
  const isNoomPremiumEligibleBySegments =
    isNoomPremiumEligibleUS() || isNoomPremiumEligibleIntl();

  return !promoCode?.promoCodeIsVip && isNoomPremiumEligibleBySegments;
};

export function isEligibleForCheckoutTimer(state = getStore().getState()) {
  // Note(Pratik) inAppIos is the only route where they saw the original timer
  // but are not eligible for coursepacks to see the new timer due to itunes addon purchase
  // limitations so we will preserve their experience
  if (isAppIosBase()) {
    return true;
  }

  if (shouldShowNoomClinicalCheckoutOffer()) {
    return false;
  }

  const originalCheckoutTimerCriteria =
    isMantle() &&
    !isEmailMain() &&
    !isEmailWinback() &&
    !state.recommendedPlan.isForcedPlan &&
    !isNewBaselineCoursePackVariant();

  const newCheckoutTimerCriteria =
    shouldShowMoodBundleCheckoutOffer() &&
    state.paymentAddon.hasOffer &&
    isNewBaselineCoursePackVariant() &&
    !isCheckoutTimerExpired();

  return originalCheckoutTimerCriteria || newCheckoutTimerCriteria;
}

export const requiresDoubleOptInForEmail = () => {
  return isCountry(["DE", "AT"]);
};

export const isWinbackUserAndNotReup = () => {
  const { winbackInfo } = getStore().getState();

  return (
    ["TRIAL", "ADVANCED"].includes(winbackInfo.winbackStatus) &&
    !winbackInfo.willReup
  );
};

isWinbackUserAndNotReup.load = () =>
  getStore()
    .dispatch(loadWinbackInfo())
    .then(() => {});

export const isWinbackUser = () => {
  const { winbackInfo } = getStore().getState();

  return ["TRIAL", "ADVANCED"].includes(winbackInfo.winbackStatus);
};

isWinbackUser.load = () =>
  getStore()
    .dispatch(loadWinbackInfo())
    .then(() => {});

export const isReUp = (state = getStore().getState()) => {
  const { winbackInfo } = state;
  return winbackInfo.willReup;
};

isReUp.load = () =>
  getStore()
    .dispatch(loadWinbackInfo())
    .then(() => {});

export const showPromoBox = checkSegments({
  noneOf: [routeConstants.exsf01, routeConstants.exsf01d, isWinback],
});

export function determinePlanOverride() {
  if (isDPP()) {
    return DPP_OVERRIDE_PLAN;
  }
  return "";
}

const isENCoursePackEligible = () => {
  return isCountry(EN_COURSE_PACK_ELIGIBLE_COUNTRIES) && isEnLanguage();
};

export const isDECoursePackEligible = () => {
  return isCountry(DE_COURSE_PACK_ELIGIBLE_COUNTRIES) && isDeLanguage();
};

const isLocaleCoursePackEligible = () => {
  return isENCoursePackEligible() || isDECoursePackEligible();
};

const isRouteCoursePackEligible = checkSegments({
  oneOf: [
    isCore,
    isReferralsBuyflow,
    isEmailMain,
    isAppAndroidBase,
    isWinback,
    isAffiliate,
  ],
});

function moodBundleOptedIn(state = getStore().getState()) {
  const { surveyAnswers } = state;
  return surveyAnswers.noomMoodBundle?.includes("yes");
}

export function shouldShowMoodBundleSurvey(state = getStore().getState()) {
  const { recommendedPlan } = state;
  return (
    isLocaleCoursePackEligible() &&
    isRouteCoursePackEligible() &&
    recommendedPlan.has_trial &&
    !isReUp(state)
  );
}

export const shouldShowCoursePackOffers = checkSegments({
  oneOf: [
    shouldShowMoodBundleCheckoutOffer,
    shouldShowNoomClinicalCheckoutOffer,
  ],
});

shouldShowMoodBundleSurvey.load = () =>
  getStore()
    .dispatch(loadWinbackInfo())
    .then(() => {});

export function shouldShowMoodBundleCheckoutOffer(
  state = getStore().getState()
) {
  const { promoCode } = state;
  return (
    shouldShowMoodBundleSurvey() &&
    !shouldShowNoomClinicalCheckoutOffer() &&
    !promoCode.promoCodeIsVip
  );
}

export const shouldPreselectMoodBundleCheckoutOffer = checkSegments({
  allOf: [shouldShowMoodBundleCheckoutOffer, moodBundleOptedIn],
});

export const acceptedCoursePackOffer = checkSegments({
  oneOf: [acceptedMoodBundleOffer, acceptedNoomClinicalOffer],
});

export function acceptedMoodBundleOffer(state = getStore().getState()) {
  const { checkout, paymentAddon } = state;
  return (
    checkout.toggledCoursePackOffer &&
    paymentAddon.hasOffer &&
    paymentAddon.product_type === "MOOD" &&
    shouldShowMoodBundleCheckoutOffer(state)
  );
}

export function acceptedNoomClinicalOffer(state = getStore().getState()) {
  const { checkout, paymentAddon } = state;
  return (
    checkout.toggledCoursePackOffer &&
    paymentAddon.hasOffer &&
    paymentAddon.product_type === "CLINICAL" &&
    shouldShowNoomClinicalCheckoutOffer(state)
  );
}

export const isNoomSummitEligible = checkSegments({
  allOf: [isUS, isEnLanguage],
  noneOf: [
    isInExperimentConflictingWithSummitCoursePack,
    isTelehealthPlan,
    userHasPurchasedNoomClinical,
    isInApp,
  ],
  oneOf: [
    isCore,
    isAffiliate,
    isReferralsBuyflow,
    isWinback,
    isEmailWinback,
    routeConstants.emailMain,
    routeConstants.freeTrial,
  ],
});

export const isNoomSummitDisplayableInStore = checkSegments({
  allOf: [isUS, isEnLanguage],
  noneOf: [isTelehealthPlan, userHasPurchasedNoomClinical, hasBoughtNoomSummit],
});

// Users who purchase Noom Summit can still visit the noom-summit-offer pagesets,
// and will be redirected to the confirmation page
export const isNoomSummitPagesetEligible = checkSegments({
  allOf: [isUS, isEnLanguage],
  noneOf: [isTelehealthPlan, userHasPurchasedNoomClinical],
});

export function hasBoughtMealPlan() {
  const { userData } = getStore().getState();
  const productIds = userData.upsell?.products || [];
  return productIds
    .filter(Boolean)
    .some((p) => isMealPlan(p) || isMealWorkoutBundle(p));
}

export function hasBoughtWorkoutPlan() {
  const { userData } = getStore().getState();
  const productIds = userData.upsell?.products || [];
  return productIds
    .filter(Boolean)
    .some((p) => isWorkoutPlan(p) || isMealWorkoutBundle(p));
}

export function hasBoughtMealAndWorkoutPlan() {
  const { userData } = getStore().getState();
  const productIds = userData.upsell?.products || [];
  return productIds.filter(Boolean).some(isMealWorkoutBundle);
}

export function hasBoughtNoomSummit() {
  const { userData } = getStore().getState();
  const productIds = userData.upsell?.products || [];
  return productIds.filter(Boolean).some(isNoomSummit);
}

/**
 * Verifies if current user is on referral routes and wants a full buyflow
 */
export const isFullPersonalizationBuyflow = checkSegments({
  noneOf: [
    ({ surveyAnswers }: CoreReduxState) => {
      return surveyAnswers.customizationChoice?.[0] === "quickStart";
    },
  ],
});

const isPreSelectedPlanLongerThan1Month = (state: CoreReduxState) => {
  const { planOptions } = state;

  return planOptions?.preSelected?.billing_cycle_in_months > 1;
};

const isPreSelectedPlanLongerThan2Months = (state: CoreReduxState) => {
  const { planOptions } = state;

  return planOptions?.preSelected?.billing_cycle_in_months > 2;
};

const isPreSelectedPlanTrial14Days = (state: CoreReduxState) => {
  const { planOptions } = state;

  return planOptions?.preSelected?.trial_duration === 14;
};

export const isPlanSelectionEligible = checkSegments({
  allOf: [
    isEmailMain,
    isUS,
    isPreSelectedPlanLongerThan1Month,
    isPreSelectedPlanTrial14Days,
  ],
});

export const shouldShowPlanSelectionSavings = checkSegments({
  allOf: [isPlanSelectionEligible, isPreSelectedPlanLongerThan2Months],
});

export const isLoadingPromoCodeModalEligible = checkSegments({
  allOf: [isUS, isEnLanguage],
  oneOf: [isEmailMain, isEmailWinback],
});

export function shouldRedirectToEmployerSurvey(
  { surveyAnswers } = getStore().getState()
) {
  const correctSurveyAnswer =
    surveyAnswers.employmentLifestyle?.includes("fullTime");
  const isCorrectTargeting = isEnLanguage() && isUS();
  const meristemConfigValue = getMeristemConfigValue(
    "redirectToEmployerSurvey"
  );

  return correctSurveyAnswer && (meristemConfigValue ?? isCorrectTargeting);
}

export function shouldRedirectToPostCheckoutContext() {
  return !!getMeristemConfigValue("usePostCheckoutContext");
}

export function shouldRedirectToHmAddonsContext() {
  return !!getMeristemConfigValue("useHmAddonsContext");
}

export function shouldRedirectHmReferralsContext() {
  return !!getMeristemConfigValue("useHmReferralsContext");
}

export const shouldShowHealthyPaceHighlight = checkSegments({
  allOf: [isUS],
  oneOf: [isMantle, isWinback],
});

export const shouldShowImportantEventHighlight = checkSegments({
  allOf: [isUS],
  oneOf: [isMantle, isWebWinback],
});

export const isEligibleForNursingOption = checkSegments({
  allOf: [isUS],
  oneOf: [isMantle],
});

// ex472b feature. PR: https://github.com/noom/buyflow-client/pull/1356
export const shouldShowAlternatePostCheckoutHeadersText = checkSegments({
  allOf: [isUS],
  oneOf: [isCore, isAffiliate, isAppBaseRoute, isReferralsBuyflow],
});

export const shouldShowUnloadWarning = checkSegments({
  allOf: [isUS],
  oneOf: [isCore, isAffiliate, isAppBaseRoute, isReferralsBuyflow],
});

export const highStressBundle = (state = getStore().getState()) => {
  const {
    manageStress,
    emotionalEating,
    eatImpulsive,
    weighStress,
    selfEsteem,
    holisticHealth,
  } = getSurveyAnswers(state);
  return [
    manageStress,
    emotionalEating,
    eatImpulsive,
    weighStress,
    selfEsteem,
    holisticHealth,
  ].some((answer) => answer && answer[0] >= 4);
};

export const isEligibleForModularBuyflow = checkSegments({
  allOf: [isEnLanguage, isMantle],
  noneOf: [isHM],
});

export const isFreeMonthsOfferEligible = checkSegments({
  allOf: [isUS, isEmailWinback, isEnLanguage],
});

export const hasExistingAcccount = checkSegments({
  oneOf: [isAppEmail],
});

export const isMultiUserPlanEligible = checkSegments({
  allOf: [isUS, isEnLanguage],
  oneOf: [
    isCore,
    isReferralsBuyflow,
    isWinback,
    isAppAndroidBase,
    isAffiliateWithTrial,
  ],
  noneOf: [shouldPreselectNoomClinicalCheckoutOffer],
});

export function multiUserPlanOptedIn(
  state: CoreReduxState = getStore().getState()
) {
  const TWO_USER_PLAN = "twoUserPlan";
  const { nominationFamily } = getSurveyAnswers(state);

  return nominationFamily?.[0] === TWO_USER_PLAN;
}

export const shouldShowPrimedSupporterExperience = checkSegments({
  allOf: [isUS, isEnLanguage],
  oneOf: [isCore, isWinback, isAffiliate, isReferralsBuyflow, isAppBaseRoute],
});

export const isReferedFromUserPortal = () => {
  return getURLParams()?.redirected_from === "userportal";
};

export const isFallbackPromotionalOfferEligible = checkSegments({
  allOf: [isUS, isEnLanguage, isEmailMain],
  noneOf: [shouldShowNoomClinicalCheckoutOffer],
});

const wasIncorrectPromoCodeEntered = (
  state: CoreReduxState = getStore().getState()
) => {
  return state?.promoCode?.promoCodeErrorType === "IncorrectPromoCode";
};

export const shouldShowFallbackPromotionalOffer = checkSegments({
  allOf: [
    isFallbackPromotionalOfferEligible,
    hasFallbackPromotionalOfferConfiguration,
    wasIncorrectPromoCodeEntered,
  ],
});

export const shouldAllowPromoCodeRemoval = () => {
  if (
    isFallbackPromotionalOfferEligible() &&
    hasFallbackPromotionalOfferConfiguration()
  ) {
    return true;
  }
  return (
    isPromoCodePersisted() &&
    isEnLanguage() &&
    (isEmailMain() || isEmailWinback())
  );
};

export function canUseShareApi() {
  return (
    "canShare" in navigator &&
    navigator.canShare({ text: "anything", url: "anything" }) &&
    "share" in navigator
  );
}

export const isBillingReminderEligible = checkSegments({
  oneOf: [
    isCore,
    isAffiliateWithTrial,
    isAppAndroidBase,
    isReferralsBuyflow,
    isAppIosBase,
  ],
  allOf: [isUS, isEnLanguage],
});
